import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Moon, Sun, TrendingUp } from 'lucide-react';
import { useTheme } from '@/lib/theme';

interface AuthLayoutProps {
  children: ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen flex">
      {/* Left side - Brand/Info */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 bg-grid-16" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
              <span className="text-2xl font-bold">Sales Tracker</span>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-md"
            >
              <h1 className="text-4xl font-bold mb-4">
                Control total de tus ventas
              </h1>
              <p className="text-lg opacity-90">
                La mejor plataforma para teleoperadores. Gestiona clientes, registra ventas y analiza resultados en tiempo real.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Animated background elements */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-white/5 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-purple-500/10 blur-3xl"></div>
      </div>

      {/* Right side - Auth form */}
      <div className="relative flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 z-20 p-2 rounded-lg bg-white/70 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-900 transition-colors"
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Sales Tracker
              </span>
            </div>
          </div>

          {/* Auth content */}
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-gray-800 p-8 shadow-2xl">
            {children}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            <p>© 2026 Sales Tracker. Plataforma para teleoperadores.</p>
            <p className="mt-1">
              <span className="opacity-75">Versión 1.0.0</span>
              <span className="mx-2">•</span>
              <span className="opacity-75">React 19 + NestJS 11</span>
              <span className="mx-2">•</span>
              <span className="opacity-75">By César</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
